import { SignIn } from '@clerk/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import {
  clearPendingRole,
  peekPendingRole,
  roleFromQueryValue,
  setPendingRole,
} from '~/features/user/pendingRoleIntent';
import { authDebug, summarizeUrl } from '~/routing/authDebug';
import { hasNestedClerkHash } from '~/routing/clerkContinuation';
import {
  CLERK_SATELLITE_URL_PARAM,
  RETURN_TO_PARAM,
  ROLE_INTENT_PARAM,
  buildAuthCompletePath,
  readAuthHandoffUrl,
  resolveSatelliteAuthCompleteUrl,
  sanitizeOAuthCompletionUrl,
  sanitizeReturnTo,
  sanitizeSatelliteReturnUrl,
} from '~/routing/returnTo';
import { paths } from '~/routing/routes';
import {
  readSessionTaskCompletionTarget,
  rememberSessionTaskCompletionTarget,
} from '~/routing/sessionTaskHandoff';
import { AuthLoading } from './AuthLoading';
import { LoginImages } from './LoginImages';
import { SatelliteAuthUnavailable } from './SatelliteAuthUnavailable';
import { useSatelliteRedirect } from './useSatelliteRedirect';

// [MCV-1762] A provider gives their name on the registration "Provider Category" step, where it
// must match their NPI record, so the card does not ask for it too. Only the name row is hidden:
// `showOptionalFields: false` would also hide the optional phone number field, which MCV-1429
// wants at sign-up (for 2FA). Clerk renders first and last name together in one row, targeted
// by the `formFieldRow__name` element key. Module-level so Clerk sees one object.
const HIDE_NAME_ROW_APPEARANCE = { elements: { formFieldRow__name: { display: 'none' } } };

export const SignInPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const clerkPath =
    location.pathname === paths.publicAuth.signInLegacy ||
    location.pathname.startsWith(`${paths.publicAuth.signInLegacy}/`)
      ? paths.publicAuth.signInLegacy
      : paths.publicAuth.signIn;
  // A managed continuation (e.g. /sign-in/factor-one, /sign-in/sso-callback)
  // is Clerk resuming a flow already in progress on THIS root render -- never
  // where a fresh choice or a stale leftover intent should be judged.
  const isManagedContinuation = location.pathname.replace(/\/+$/, '') !== clerkPath;
  // A hash-routed continuation (`/sign-in#/factor-one`) keeps the bare
  // pathname, so the root check alone would read it as a fresh entry.
  const isFreshRootEntry = !isManagedContinuation && !hasNestedClerkHash(location.hash);

  // [MCV-1666][MCV-1667] The role choice made on LoginOrCreateAccountPage
  // arrives here as `?role=provider`/`?role=employer`. It must be written to
  // sessionStorage BEFORE `useSatelliteRedirect()` runs: on HLX that hook
  // bounces this render to the MedCredVault primary and only returns to the
  // HLX origin afterward, where RoleSelectPage reads the HLX origin's
  // sessionStorage -- so the write has to happen on THIS render, on THIS
  // origin, before the hook below can leave it. A ref (not an effect) makes
  // it happen synchronously during render and only once, even across
  // re-renders caused by the redirect itself.
  //
  // A ROOT render (bare /sign-in, no role param) with no fresh choice must
  // clear any leftover intent: without this, a visitor who chose Provider,
  // signed into an existing account (never visiting RoleSelectPage, so the
  // intent was never consumed), signed out, then opened bare /sign-in and
  // created a NEW account would have that stale intent pass the freshness
  // check and route the new account into provider registration. A managed
  // continuation must leave storage untouched -- it is mid-flow, not a fresh
  // entry.
  const roleParam = searchParams.get(ROLE_INTENT_PARAM);
  const role = roleFromQueryValue(roleParam);
  // Keyed on the location, not the mount: React Router can move this page
  // between bare /sign-in and /sign-in?role=… without remounting it.
  const locationKey = `${location.pathname}${location.search}${location.hash}`;
  const pendingRoleWrittenFor = useRef<string | null>(null);
  if (pendingRoleWrittenFor.current !== locationKey) {
    pendingRoleWrittenFor.current = locationKey;
    if (role) {
      setPendingRole(role);
    } else if (isFreshRootEntry) {
      clearPendingRole();
    }
  }

  // [MCV-1762] Clerk's own sub-steps (`/sign-in/create`, where a new account fills in its
  // sign-up fields) drop `?role=`, so a continuation reads the choice back from storage. A fresh
  // root entry has just written or cleared that choice above, so storage is current either way.
  // Employers (the HLX hop to this card carries no role) keep the Clerk name fields, which are
  // the only source of their names.
  const isProviderFlow = role === 'PROVIDER' || peekPendingRole() === 'PROVIDER';

  const { redirecting, blocked } = useSatelliteRedirect();
  const [mounted, setMounted] = useState(false);

  const returnTo = useMemo(
    () => sanitizeReturnTo(searchParams.get(RETURN_TO_PARAM), window.location.origin),
    [searchParams]
  );
  const satelliteReturnTo = useMemo(
    () => sanitizeSatelliteReturnUrl(searchParams.get(CLERK_SATELLITE_URL_PARAM)),
    [searchParams]
  );
  const satelliteAuthCompleteUrl = useMemo(
    () => resolveSatelliteAuthCompleteUrl(searchParams.get(CLERK_SATELLITE_URL_PARAM)),
    [searchParams]
  );
  const authCompletePath = buildAuthCompletePath(returnTo);
  const defaultFallbackRedirectUrl = satelliteAuthCompleteUrl ?? authCompletePath;
  const explicitRedirectUrl = useMemo(
    () =>
      sanitizeOAuthCompletionUrl(
        readAuthHandoffUrl(location.search, location.hash),
        window.location.origin
      ),
    [location.hash, location.search]
  );
  const sessionTaskCompletionTarget =
    explicitRedirectUrl ??
    (isManagedContinuation ? readSessionTaskCompletionTarget() : null) ??
    defaultFallbackRedirectUrl;
  const fallbackRedirectUrl = isManagedContinuation
    ? sessionTaskCompletionTarget
    : defaultFallbackRedirectUrl;
  const forceRedirectUrl = satelliteAuthCompleteUrl
    ? undefined
    : isManagedContinuation
      ? sessionTaskCompletionTarget
      : returnTo
        ? authCompletePath
        : undefined;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    rememberSessionTaskCompletionTarget(sessionTaskCompletionTarget);
  }, [sessionTaskCompletionTarget]);

  useEffect(() => {
    authDebug('sign-in.redirect_props', {
      hasSatelliteReturnTo: Boolean(satelliteReturnTo),
      returnTo,
      satelliteReturnTo: summarizeUrl(satelliteReturnTo),
      satelliteAuthCompleteUrl: summarizeUrl(satelliteAuthCompleteUrl),
      forceRedirectUrl,
      fallbackRedirectUrl: summarizeUrl(fallbackRedirectUrl),
    });
  }, [
    fallbackRedirectUrl,
    forceRedirectUrl,
    returnTo,
    satelliteAuthCompleteUrl,
    satelliteReturnTo,
  ]);

  if (blocked) {
    return <SatelliteAuthUnavailable />;
  }

  if (redirecting) {
    return <AuthLoading message="Connecting you to secure authentication…" />;
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-primary-t-950 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        <div
          className={`flex flex-col items-center justify-center motion-safe:transition-opacity motion-safe:duration-500 motion-safe:delay-200 motion-safe:ease-out ${
            mounted ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <SignIn
            routing="path"
            path={clerkPath}
            withSignUp
            appearance={isProviderFlow ? HIDE_NAME_ROW_APPEARANCE : undefined}
            // [MCV-1666] If Clerk leaves the combined card for /sign-up, the
            // choice must travel with it -- SignUpPage clears stale intent on a
            // fresh visit, and this param is what tells it the visit is not stale.
            signUpUrl={
              role && roleParam
                ? `${paths.publicAuth.signUp}?${new URLSearchParams({ [ROLE_INTENT_PARAM]: roleParam })}`
                : paths.publicAuth.signUp
            }
            signUpForceRedirectUrl={forceRedirectUrl}
            signUpFallbackRedirectUrl={fallbackRedirectUrl}
            forceRedirectUrl={forceRedirectUrl}
            fallbackRedirectUrl={fallbackRedirectUrl}
          />
        </div>

        <div
          className={`flex flex-col items-center justify-center motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out ${
            mounted ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <LoginImages />
        </div>
      </div>
    </div>
  );
};
